import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { faQuestionCircle } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface LabelIF extends ValidatingFormComponent<string | React.ReactNode> {
  component: 'LabelFormComponent';
  xstyle?: stylex.StyleXStyles;
  noMarginBottom?: boolean;
  onIconClick?: () => any;
  valueCustomParams?: Record<string, string | number>;
  onClick?: () => void;
  skipTranslation?: boolean;
  textClassNames?: string;
}

const LabelFormComponentUnwrapped = ({
  value,
  tooltip,
  onIconClick,
  onClick,
  testId,
  extraClassNames,
  xstyle,
  noMarginBottom,
  tooltipDelay,
  tooltipPlacement,
  valueCustomParams,
  skipTranslation = false,
  textClassNames,
  textXstyle,
}: LabelIF) => {
  const { t } = useTranslation();

  const textContent = _.isString(value) && !skipTranslation ? t(value, valueCustomParams) : value;

  return (
    <div
      {...stylex.props(styles.label, !noMarginBottom && styles.spaced, xstyle)}
      className={clsx(stylex.props(styles.label, !noMarginBottom && styles.spaced, xstyle).className, extraClassNames)}
      onClick={onClick}
      data-testid={testId}>
      <span {...stylex.props(textXstyle)} className={clsx(stylex.props(textXstyle).className, textClassNames)}>
        {textContent}
      </span>
      {tooltip && (
        <Icon
          icon={faQuestionCircle}
          large={true}
          tooltip={t(tooltip)}
          tooltipDelay={tooltipDelay}
          tooltipPlacement={tooltipPlacement}
          xstyle={styles.helpIcon}
          testId={`${testId}_icon`}
          onClick={_.isFunction(onIconClick) ? onIconClick : _.noop}
        />
      )}
    </div>
  );
};

export const LabelFormComponent = React.memo(
  LabelFormComponentUnwrapped,
  (prev, next) =>
    !(
      prev.value !== next.value ||
      prev.tooltip !== next.tooltip ||
      prev.extraClassNames !== next.extraClassNames ||
      prev.xstyle !== next.xstyle ||
      prev.textXstyle !== next.textXstyle ||
      prev.textClassNames !== next.textClassNames
    ),
);

const styles = stylex.create({
  helpIcon: { paddingLeft: '.3125rem' },
  label: { display: 'flex', flexDirection: 'row' },
  spaced: { marginBottom: 5 },
});
